import { consentChangeAction, onConsentChange } from './polarisConsent';
import type { ConsentState } from './polarisConsent.types';

export type PageNavigation = Readonly<{
  reload: () => void;
  assign: (url: string) => void;
}>;

const browserNavigation: PageNavigation = {
  reload: () => window.location.reload(),
  assign: url => window.location.assign(url),
};

type HistoryUpdate = History['pushState'];

/**
 * The same-origin page a history update moves to, or `null` when it should stay a plain history
 * update: no URL, a URL that is not plain same-origin http(s) (another origin, `javascript:`,
 * `blob:`, credentials, or anything in a sandboxed frame whose origin is `null`), or a change to the
 * hash alone.
 */
const pageChangeTarget = (url: string | URL | null | undefined): string | null => {
  if (url === undefined || url === null) return null;
  let target: URL;
  try {
    target = new URL(url, window.location.href);
  } catch {
    return null;
  }
  const isWebUrl = target.protocol === 'http:' || target.protocol === 'https:';
  if (!isWebUrl || target.username || target.password || target.origin !== window.location.origin) return null;
  if (target.pathname === window.location.pathname && target.search === window.location.search) return null;
  return target.href;
};

const loadFullyInstead = (update: HistoryUpdate, load: (url: string) => void): HistoryUpdate =>
  function (this: History, data: unknown, unused: string, url?: string | URL | null) {
    const target = pageChangeTarget(url);
    if (target === null) {
      update.call(this, data, unused, url);
      return;
    }
    load(target);
  };

/**
 * Makes the next in-app page change a full page load. joy-web mounts several react-router
 * `BrowserRouter`s, and every one of them navigates through `history.pushState` and listens to
 * `popstate`, so this hooks those rather than any one router. `replaceState` is left alone: pages
 * call it as the visitor scrolls or to tidy the query string, where a reload would lose their place
 * and form state, and a missed one only delays the newly granted destinations by one navigation.
 */
const loadFullyOnNextNavigation = (page: PageNavigation) => {
  window.history.pushState = loadFullyInstead(window.history.pushState, page.assign);
  const pageKey = () => window.location.pathname + window.location.search;
  const armedOn = pageKey();
  window.addEventListener('popstate', () => {
    if (pageKey() !== armedOn) page.reload();
  });
};

/**
 * Applies consent choices the visitor makes after Segment loaded with `loaded`: a withdrawal
 * reloads the page, because Segment cannot unload a destination it has loaded; a grant waits for the
 * visitor's next page change, which becomes a full page load.
 */
export const followConsentChanges = (loaded: ConsentState, readConsent: () => ConsentState, page: PageNavigation = browserNavigation): void => {
  let fullLoadArmed = false;
  onConsentChange(() => {
    const action = consentChangeAction(loaded, readConsent());
    if (action === 'reload') {
      page.reload();
    } else if (action === 'next-navigation' && !fullLoadArmed) {
      fullLoadArmed = true;
      loadFullyOnNextNavigation(page);
    }
  });
};
